import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
  useDisclosure,
} from "@nextui-org/react";
import React, { useState } from "react";
import { TbAbc } from "react-icons/tb";
import { FieldList } from "../../utils/constant";
import EditAddModal from "./EditAddModal";
import UnifiedDeleteModal from "../../components/UnifiedDeleteModal";
import { generateUniqueId } from "../../utils/frombuilder.utils";

const FieldInfoTable = ({
  fieldInfoList,
  setFieldInfoList,
  headersList,
  tableType = "",
  frameWorkList,
  frameWorkDetails,
  category,
}) => {

  const [editItem, setEditItem] = useState({});
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onOpenChange: onDeleteOpenChange,
  } = useDisclosure();
  const [itemToDelete, setItemToDelete] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const headers = headersList || ["Order", "Name", "Type", ""];

  const updateFieldHandler = (item) => {
    const newFieldInfoList = fieldInfoList.map((data) => {
      if (data.id === item.id) {
        // Ensure the updated field maintains its displayOrder
        return {
          ...item,
          displayOrder: data.displayOrder !== undefined ? data.displayOrder : data.id
        };
      } else return data;
    });

    setFieldInfoList(newFieldInfoList);
  };

  const confirmDeleteHandler = (item) => {
    setItemToDelete(item);
    onDeleteOpen();
  };

  const deleteHandler = () => {
    if (itemToDelete) {
      const newFieldInfoList = fieldInfoList.filter((item) => item.id !== itemToDelete.id);
      setFieldInfoList(newFieldInfoList);
      setItemToDelete(null);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const items = Array.from(fieldInfoList);
    const [draggedItem] = items.splice(draggedIndex, 1);
    items.splice(dropIndex, 0, draggedItem);

    // Update displayOrder for all fields after reordering
    const updatedItems = items.map((item, index) => ({
      ...item,
      displayOrder: index
    }));

    setFieldInfoList(updatedItems);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Use the current order instead of sorting by ID
  const displayList = fieldInfoList;

  return (
    <>
      {fieldInfoList.length > 0 && (
        <div className="m-4">
          <Table removeWrapper aria-label="Form fields configuration table">
            <TableHeader>
              {headers.map((header, headerIndex) => (
                <TableColumn key={headerIndex}>{header}</TableColumn>
              ))}
            </TableHeader>
            <TableBody>
              {displayList &&
                displayList.map((item, index) => {
                  const isDragging = draggedIndex === index;
                  const isDragOver = dragOverIndex === index;
                  
                  return (
                    <TableRow
                      key={item.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`
                        ${isDragging ? 'opacity-50' : ''}
                        ${isDragOver ? 'border-t-2 border-emerald-500' : ''}
                        cursor-move
                      `}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="cursor-grab hover:cursor-grabbing mr-2">
                            <lord-icon
                              src="https://cdn.lordicon.com/qzfzkbxi.json"
                              trigger="hover"
                              
                              style={{ width: "16px", height: "16px" }}
                            ></lord-icon>
                          </div>
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">
                            #{index + 1}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="flex items-center gap-2">
                        {FieldList[item.Type]?.icon || (
                          <TbAbc size={"30px"} color="#7828c8" />
                        )}
                        <span>
                          {item.Title}{" "}
                          {item.Required && (
                            <span style={{ color: "#f31b66" }}> *</span>
                          )}
                        </span>
                      </TableCell>

                      <TableCell>
                        {item.Type === "link" ? item.link : item.name}
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center justify-end gap-4">
                          <div
                            className="cursor-pointer flex items-center justify-center"
                            onClick={() => {
                              setEditItem(item);
                              onOpen();
                            }}
                          >
                            <Tooltip size="sm" content="Edit field">
                              <lord-icon
                                src="https://cdn.lordicon.com/lzgmgrnn.json"
                                trigger="hover"
                                
                                style={{ width: "20px", height: "20px" }}
                              ></lord-icon>
                            </Tooltip>
                          </div>
                          <div
                            className="flex gap-4 text-center justify-end cursor-pointer"
                            onClick={() => confirmDeleteHandler(item)}
                          >
                            <Tooltip size="sm" content="Delete field">
                              <lord-icon
                                src="https://cdn.lordicon.com/wpyrrmcq.json"
                                trigger="hover"
                                
                                style={{ width: "20px", height: "20px" }}
                              ></lord-icon>
                            </Tooltip>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
      )}
      {isOpen && (
        <EditAddModal
          onSubmitHandler={updateFieldHandler}
          isOpen={isOpen}
          onOpen={onOpen}
          onOpenChange={onOpenChange}
          fieldType={editItem?.Type}
          id={Date.now()}
          item={editItem}
          frameWorkDetails={frameWorkDetails}
          frameWorkList={frameWorkList}
          category={category}
          existingFields={fieldInfoList}
        />
      )}
      <UnifiedDeleteModal
        isOpen={isDeleteOpen}
        onOpenChange={onDeleteOpenChange}
        onConfirm={deleteHandler}
        title="Delete Field"
        message="Are you sure you want to delete"
        confirmText="Delete Field"
        cancelText="Cancel"
        itemName={itemToDelete?.Title}
      />
    </>
  );
};

export default FieldInfoTable;
